import { Component, computed, HostListener, inject, Input, OnDestroy, OnInit } from '@angular/core';
import { GridLayoutComponent, GridLayoutItemComponent } from '@progress/kendo-angular-layout';
import { LabelComponent } from '@progress/kendo-angular-label';
import { ErrorComponent, FormFieldComponent, NumericTextBoxComponent } from '@progress/kendo-angular-inputs';
import { LanguageService } from 'src/app/services/language.service';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { DropDownListComponent, ItemTemplateDirective } from '@progress/kendo-angular-dropdowns';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { logger } from 'src/app/utils';
import { Accessory } from 'src/app/models/product.model';
import { Translation } from 'src/app/models/translation.model';
import { SpacerComponent } from 'src/app/ui/atoms/spacer/spacer.component';
import { TestDataService } from 'src/app/services/testData.service';
import { Subscription } from 'rxjs';
import { FormService } from 'src/app/services/form.service';
import { EXTRA_ACCESSORIES_IN_BIKE_SELECTION_PAGE } from 'src/app/app.constants';
import { DialogComponent } from '@progress/kendo-angular-dialog';

@Component({
    selector: 'app-accessories-selection',
    imports: [
        LabelComponent,
        GridLayoutComponent,
        GridLayoutItemComponent,
        NumericTextBoxComponent,
        FormsModule,
        TranslatePipe,
        ReactiveFormsModule,
        FormFieldComponent,
        DropDownListComponent,
        SpacerComponent,
        ItemTemplateDirective,
        DialogComponent
    ],
    templateUrl: './accessories-selection.component.html',
    styleUrl: './accessories-selection.component.scss'
})
export class AccessoriesSelectionComponent implements OnInit, OnDestroy {
  private testDataService = inject(TestDataService);
  private testDataSubscription: Subscription;

  private languageService = inject(LanguageService);
  private translateService = inject(TranslateService);
  private formService = inject(FormService);

  @Input({required: true}) formId: string;

  formGroup = new FormGroup({} as any);
  smallWidth = false;

  lang = computed(() => this.languageService.currentLanguage());

  bikes = this.formService.form().bikes || [];
  extraAccessories = (this.formService.accessories() || [])
    .filter(e => !EXTRA_ACCESSORIES_IN_BIKE_SELECTION_PAGE.includes(e.bikeType));

  emptyAccessoryModel: { bikeType?: number, translations: Translation };

  limitError = false;

  constructor() {
    const noAccessoryText = this.translateService.instant('accessoriesSelection.noneAccessory');
    this.emptyAccessoryModel = {
      translations: {
        id: -1,
        en: noAccessoryText,
        de: noAccessoryText,
        it: noAccessoryText
      }
    }
  }

  ngOnInit() {
    this.onResize();

    this.formService.accessoriesViewed.set(true);

    this.formService.update({
      ...this.formService.form(),
      previousBikes: this.formService.form().bikes,
    });

    logger.t('extra accessories', this.extraAccessories);
    const chosenAccessories = this.formService.form().accessories
      ?.filter(c => !EXTRA_ACCESSORIES_IN_BIKE_SELECTION_PAGE.includes(c.bikeType));

    this.extraAccessories
      .filter(ea => !EXTRA_ACCESSORIES_IN_BIKE_SELECTION_PAGE.includes(ea.bikeType))
      .forEach(accessory => {
      const quantity = chosenAccessories?.find(c =>
        c.bikeType === this.extraAccessories.find(e => e.bikeType === accessory.bikeType)?.bikeType
      )?.quantity;
      const accessoryGroup = new FormGroup<any>({quantity: new FormControl(quantity)});
      this.formGroup.addControl(`accessory-${accessory.bikeType}`, accessoryGroup);
    });

    this.bikes.forEach(bike => {
      const bikeControl = new FormGroup({accessories: new FormControl(bike.accessories?.[0])});
      if (!bike.supportedAccessories || bike.supportedAccessories.length === 0) {
        bikeControl.disable();
      }
      this.formGroup.addControl(`bike-${bike.id}`, bikeControl);
    });

    this.formGroup.valueChanges.subscribe(() => this.buildDataAccessories())

    this.buildDataAccessories();
    this.initTestDataObserver();
  }

  private initTestDataObserver() {
    this.testDataSubscription = this.testDataService.fillDataObserver.subscribe(() => {
      if (document.getElementById(this.formId)) {
        const extraAccessoriesForms = Object.keys(this.formGroup.controls).filter(key => key.startsWith('accessory-'));
        for (const extraAccForm of extraAccessoriesForms) {
          const accessoryGroup = this.formGroup.get(extraAccForm) as FormGroup;
          const accessory = this.extraAccessories.find(a => `accessory-${a.bikeType}` === extraAccForm);
          if (accessory) {
            accessoryGroup.get('quantity')?.setValue(0);
            accessoryGroup.get('quantity')?.setValue(undefined);
          }
        }

        for (const extraAccForm of extraAccessoriesForms) {
          const accessoryGroup = this.formGroup.get(extraAccForm) as FormGroup;
          const accessory = this.extraAccessories.find(a => `accessory-${a.bikeType}` === extraAccForm);
          if (accessory) {
            let randomQuantity: any = Math.floor(Math.random() * accessory.availability);
            if (randomQuantity === 0) randomQuantity = undefined;
            accessoryGroup.get('quantity')?.setValue(randomQuantity);
          }
        }

        const bikeForms = Object.keys(this.formGroup.controls).filter(key => key.startsWith('bike-'));
        for (const bikeForm of bikeForms) {
          const bikeGroup = this.formGroup.get(bikeForm) as FormGroup;
          const bike = this.formService.form().bikes?.find(b => `bike-${b.id}` === bikeForm);
          if (bike && bike.supportedAccessories?.length > 0) {
            const randomIndex = Math.floor(Math.random() * bike.supportedAccessories.length) - 1;
            const randomAccessory = randomIndex === -1 ? undefined : bike.supportedAccessories[randomIndex];
            if (randomAccessory) {
              bikeGroup.get('accessories')?.setValue(randomAccessory);
            } else {
              bikeGroup.get('accessories')?.reset();
            }
          }
        }

        this.formGroup.markAllAsTouched();
      }
    });
  }

  @HostListener('window:resize', ['$event'])
  onResize() {
    this.smallWidth = window.innerWidth < 482;
  }

  submit() {
    this.formGroup.markAllAsTouched();
    this.buildDataAccessories();
    this.checkLimitAccessories();
  }

  private checkLimitAccessories() {
    const chosenAccessories = this.formService.form().bikes?.flatMap((b) => b.accessories || []) || [];
    const uniqueAccessories = [...new Map(chosenAccessories.map(item => [item.id, item])).values()];
    for(const acc of uniqueAccessories) {
      const occurrences = chosenAccessories.filter(a => a.id === acc.id).length;
      if (occurrences > acc.availability) {
        this.limitError = true;
        return;
      } else {
        if (this.limitError) this.limitError = false;
      }
    }
  }

  private buildDataAccessories() {
    const aData = this.formGroup.getRawValue();
    const aArray: Accessory[] = [];

    Object.keys(aData)
      .filter(key => key.startsWith('accessory-') && aData[key].quantity > 0)
      .forEach(key => {
        const aType = parseInt(key.split('-')[1]);
        const accessory = aData[key];
        aArray.push({
          ...Object.create(this.extraAccessories).find((a: Accessory) => a.bikeType === aType)!,
          quantity: accessory.quantity
        });
      });

    Object.keys(aData)
      ?.filter(key => key.startsWith('bike-'))
      ?.forEach(key => {
        const bikeId = parseInt(key.split('-')[1]);
        const bike = this.formService.form().bikes?.find(b => b.id === bikeId);
        // for kendo multi-select-dropdown use aData[key].accessories.slice();
        if (bike) {
          let accessory = aData[key].accessories;
          if (accessory?.id) { // for exclude empty model
            accessory.quantity = 1;
          } else {
            accessory = undefined;
          }
          bike.accessories = accessory ? [accessory] : [];
        }
      });


    const accInBikePage = this.formService.form().accessories
      ?.filter(a => EXTRA_ACCESSORIES_IN_BIKE_SELECTION_PAGE.includes(a.bikeType)) || [];

    this.formService.update({
      ...this.formService.form(),
      accessories: [...accInBikePage, ...aArray],
      previousSameRental: true
    });
  }

  onMouseWheel = () => null;

  ngOnDestroy() {
    this.testDataSubscription?.unsubscribe();
  }

}
